import {
  Box,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';
import { StyledButton } from '../ui';

/**
 * @typedef {Object} UseCase
 * @property {string} id - Unique identifier
 * @property {string} title - Use case title
 * @property {string} subtitle - Use case subtitle/audience
 * @property {string} description - Use case description
 * @property {string[]} benefits - List of benefits
 * @property {React.ReactNode} icon - Use case icon
 * @property {string} ctaText - Call-to-action text
 * @property {string} ctaHref - Call-to-action link
 */

/**
 * @typedef {Object} UseCasesProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 */

/**
 * Use Cases / Who It's For Section
 * Inspired by Domo's industry solutions
 *
 * @param {UseCasesProps} props - Component props
 * @returns {JSX.Element} Use cases section
 */
function UseCases({
  sectionId = 'use-cases',
  headline = 'One Business Intelligence Layer. Three Financial Experiences.',
  subheadline = 'Whether you lead the finance team or you\u2019re an engineer collaborating with the FinOps lead, EllaFin\u2122 speaks your language.',
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /** @type {UseCase[]} */
  const useCases = [
    {
      id: 'cfo',
      title: 'CFOs & Finance Leadership',
      subtitle: 'Strategic visibility into cloud spend',
      description:
        'Get visibility into cloud spend in financial terms you understand. Make informed decisions about cloud investments with data aligned to your P&L and balance sheet.',
      benefits: [
        'Intelligent Forecasting',
        'Business Impact of Technology Investment',
        'Unified Financial Data Insight',
        'Purpose-Built AI Financial Agents',
      ],
      icon: (
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/images/ella-fin-logo-cropped.jpg`}
          alt="EllaFin™"
          sx={{ width: 100, height: 'auto' }}
        />
      ),
      ctaText: 'Learn More',
      ctaHref: '/product#cfo',
    },
    {
      id: 'fpa',
      title: 'FP&A Teams',
      subtitle: 'Accurate Financial Analysis',
      description:
        'Stop guessing at cloud costs in your budgets. Get accurate, granular cost data that integrates with your planning models and variance analysis.',
      benefits: [
        'Predictive Budget & Spend Forecasting',
        'Natural Language What if Scenarios',
        'CxO Ready Report Generation',
        'Real-Time Variance Intelligence',
      ],
      icon: (
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/images/ella-fin-logo-cropped.jpg`}
          alt="EllaFin™"
          sx={{ width: 100, height: 'auto' }}
        />
      ),
      ctaText: 'Learn More',
      ctaHref: '/product#fpa',
    },
    {
      id: 'finops',
      title: 'FinOps Practitioners',
      subtitle: 'AWS Cost Management powered by nOps',
      description:
        'Complete control over AWS cost with autonomous commitment management and spend insight that drives financial accountability with engineering teams.',
      benefits: [
        'Automated Cost Allocation & Unit Economics',
        'Real-Time Savings Plan & RI Management',
        'ML-Powered Forecasting & Anomaly Detection',
        'Autonomous Workload & Waste Optimization',
      ],
      icon: (
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/images/ella-fin-logo-cropped.jpg`}
          alt="EllaFin™"
          sx={{ width: 100, height: 'auto' }}
        />
      ),
      ctaText: 'Learn More',
      ctaHref: '/product#finops',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /**
   * Handle tab change
   * @param {React.SyntheticEvent} _event - Event object (unused)
   * @param {number} newValue - New tab index
   */
  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const activeUseCase = useCases[activeTab];

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="Use cases"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.section,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          <Text
            sx={{
              fontSize: { xs: '1rem', md: '1.2rem' },
              color: '#6c757d',
              maxWidth: '650px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {subheadline}
          </Text>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered={!isMobile}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            aria-label="Use case tabs"
            sx={{
              mb: 4,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: '3px',
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '1rem' },
                color: '#6c757d',
                minWidth: { xs: 'auto', md: '180px' },
                px: { xs: 2, md: 3 },
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {useCases.map((useCase, index) => (
              <Tab
                key={useCase.id}
                label={useCase.title}
                id={`usecase-tab-${index}`}
                aria-controls={`usecase-panel-${index}`}
              />
            ))}
          </Tabs>

          {/* Tab Content */}
          <Box
            role="tabpanel"
            id={`usecase-panel-${activeTab}`}
            aria-labelledby={`usecase-tab-${activeTab}`}
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: '24px',
              p: { xs: 3, md: 5 },
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              overflow: 'hidden',
              transition: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 4, md: 6 },
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: '100px',
                  height: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {activeUseCase.icon}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {activeUseCase.subtitle}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    mb: 2,
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                  }}
                >
                  {activeUseCase.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#4a4a4a',
                    lineHeight: 1.7,
                    mb: 3,
                    fontSize: '1.05rem',
                  }}
                >
                  {activeUseCase.description}
                </Typography>

                {/* Benefits List */}
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    mb: 4,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 1.5,
                  }}
                >
                  {activeUseCase.benefits.map((benefit, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        color: '#4a4a4a',
                        fontSize: '0.95rem',
                      }}
                    >
                      <Box
                        sx={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: theme.palette.secondary.main,
                          clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                          mt: 0.75,
                          flexShrink: 0,
                        }}
                      />
                      {benefit}
                    </Box>
                  ))}
                </Box>

                <StyledButton
                  variant="outlined"
                  size="medium"
                  href={activeUseCase.ctaHref}
                >
                  {activeUseCase.ctaText}
                </StyledButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default UseCases;
